// src/AssetManager/useAssetPreview.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchAssetById } from '../utils/frontend_assetManager';

// Simple in-memory cache for this session.
// Map<cacheKey, { preview: object, exp: number }>
const PREVIEW_CACHE = new Map();

// Default TTL for signed URLs (match server, leave buffer)
const TTL_MS = 18 * 60 * 1000; // 18 minutes

/**
 * useAssetPreview
 * Resolve { assetId } → short-lived preview URL + aspect.
 * Keeps Storage/signing logic on the server.
 *
 * Fails CLOSED:
 *  - Clears any previous URL when a new fetch starts
 *  - Leaves url=null when the CF returns 404 or throws
 *
 * @param {{ assetId?: string, id?: string }} assetRef
 * @returns {{
 *   loading: boolean,
 *   error: string|null,          // human-ish message (or null)
 *   url: string|null,            // signed URL or null
 *   aspect: number|null,         // safe aspect (or null)
 *   name: string,                // display name from meta/name
 *   meta: any|null,              // meta block from CF (or null)
 *   raw: any                     // full payload
 * }}
 */
export function useAssetPreview(assetRef) {
  const { getToken } = useAuth();
  const assetId = assetRef?.assetId || assetRef?.id || null;

  const [state, setState] = useState({
    loading: Boolean(assetId),
    error: null,
    data: null,
  });

  // avoid setState after unmount
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Invalidate preview immediately on asset deletion elsewhere
  useEffect(() => {
    if (!assetId) return;
    const onDeleted = (e) => {
      const deletedId = e?.detail?.assetId;
      if (deletedId && deletedId === assetId) {
        // drop cache & clear local preview
        for (const key of PREVIEW_CACHE.keys()) {
          if (key.startsWith(`${assetId}@`)) PREVIEW_CACHE.delete(key);
        }
        if (!mounted.current) return;
        setState({ loading: false, error: 'Asset deleted', data: null });
      }
    };
    window.addEventListener('asset:deleted', onDeleted);
    return () => window.removeEventListener('asset:deleted', onDeleted);
  }, [assetId]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

async function run() {
  // SWR: keep prior data to avoid url flaps; only toggle loading / error
  if (!assetId) {
    // nothing to fetch → fully reset
    setState({ loading: false, error: null, data: null });
    return;
  }

  // ────────────────────────────────────────────────────────────────
  // ENTERPRISE: submit freeze / cooldown gate
  // - Prevents post-submit preview fetch from stealing cycles
  // - Prevents calling getAssetById while the asset is still processing
  // - NOTE: This matches the global flags created by src/utils/useAssetPreview.js
  //   • window.__ASSET_PREVIEW_FROZEN__ : boolean
  //   • window.__ASSET_PREVIEW_UNTIL__  : epoch ms
  // ────────────────────────────────────────────────────────────────
  try {
    if (typeof window !== "undefined") {
      const frozen = window.__ASSET_PREVIEW_FROZEN__ === true;
      const until  = Number(window.__ASSET_PREVIEW_UNTIL__ || 0);

      if (frozen || Date.now() < until) {
        // SWR policy: do NOT drop existing data; just stop loading
        if (!cancelled && mounted.current) {
          setState((s) => ({ ...s, loading: false }));
        }
        return;
      }
    }
  } catch (_) {
    // non-fatal; if gate fails, we continue as normal
  }

  // 1) decide DPR once (server may choose 400 vs 800 grid based on it)
  const dpr =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  // 2) cache (keyed by assetId + dpr so we don’t reuse a 1x URL on 2x)
  const now = Date.now();
  const cacheKey = `${assetId}@${Math.round(dpr * 10) / 10}`;
  const cached = PREVIEW_CACHE.get(cacheKey);

  if (cached && cached.exp > now) {
    if (!cancelled && mounted.current) {
      setState({ loading: false, error: null, data: cached.preview });
    }
    return;
  }

  // fetch fresh
  if (!cancelled && mounted.current) {
    // SWR: don’t drop data to null; just mark loading
    setState((s) => ({ ...s, loading: true, error: null }));
  }

  try {
    const token = await getToken(false);
    if (!token) throw new Error('Unauthorized');

    const preview = await fetchAssetById(
      assetId,
      token,
      {
        // Prefer crisper grid (server picks 400/800 based on DPR)
        includeUrls: true,
        dpr,
        includeMeta: true,
      },
      { signal: ctrl.signal }
    );

    PREVIEW_CACHE.set(cacheKey, { preview, exp: now + TTL_MS });

    if (!cancelled && mounted.current) {
      setState({ loading: false, error: null, data: preview });
    }
  } catch (err) {
    if (cancelled || !mounted.current) return;

    const msg = err?.message || String(err);
    let human = 'Failed to load preview';
    if (err?.name === 'AbortError') human = 'Request aborted';
    else if (/unauthor/i.test(msg)) human = 'Unauthorized';
    else if (/not\s*found|404/i.test(msg)) human = 'Asset not found';

    setState((s) => ({
      loading: false,
      error: human,
      data: /not\s*found|404/i.test(msg) ? null : (s.data || null),
    }));
  }
}

    run();

    return () => {
      cancelled = true;
      try {
        ctrl.abort();
      } catch (_) {}
    };
  }, [assetId, getToken]);

  const result = useMemo(() => {
    const d = state.data || {};
    const m = d.meta || null;

    return {
      loading: state.loading,
      error: state.error,
      url: d.previewUrl || null,
      aspect: typeof d.aspect === 'number' ? d.aspect : null,
      // Prefer server displayName if present; fallback to name
      name: (m && m.displayName) || d.name || '',
      // Hand back the meta block for tooltips (filename, size, dims, line)
      meta: m, // ← { displayName, sizeKB, width, height, ... }
      raw: d,  // full payload if a caller needs more
    };
  }, [state]);

  return result;
}
